import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Paper,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { Send as SendIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import React from "react";
import { CompanyStampListItemDto } from "@/dto/CompanyStampDto";
import { useCompanySelection } from "./useCompany";

/**
 * DoJangSendSection Props
 */
interface DoJangSendSectionProps {
  csvFile: File | null;
  isSending: boolean;
  sendProgress: number;
  onSendEmails: () => void;
  onDoJangChange: (doJangId: string, doJangName: string) => void;
}

/**
 * 도장 선택 및 발송 섹션
 * @description 도장 선택 및 이메일 대량 발송 컴포넌트입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export const DoJangSendSection = ({
  csvFile,
  isSending,
  sendProgress,
  onSendEmails,
  onDoJangChange,
}: DoJangSendSectionProps) => {
  // 커스텀 훅으로 도장 관련 로직 분리
  const {
    doJangList,
    isLoadingDoJang,
    selectedDoJangId,
    selectedDoJang,
    error,
    handleDoJangSelection,
    filterDoJangOptions,
    refetchDoJangList,
    stats,
  } = useCompanySelection();

  /**
   * Autocomplete 선택 변경 핸들러
   */
  const handleDoJangChange = (
    event: React.SyntheticEvent,
    newValue: CompanyStampListItemDto | null
  ) => {
    if (newValue) {
      handleDoJangSelection(newValue.comId);
      onDoJangChange(newValue.comId, newValue.comNm);
    } else {
      handleDoJangSelection("");
      onDoJangChange("", "");
    }
  };

  /**
   * 새로고침 버튼 핸들러
   */
  const handleRefresh = async () => {
    await refetchDoJangList();
  };

  return (
    <Paper elevation={0} variant="outlined">
      <Box sx={{ p: 3 }}>
        {/* 헤더 섹션 */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            🥋 Step 2: 도장 선택 및 발송
          </Typography>
          <IconButton
            onClick={handleRefresh}
            disabled={isLoadingDoJang || isSending}
            size="small"
            title="도장 목록 새로고침"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3}>
          멤버를 등록할 도장을 선택하고 이메일을 발송합니다.
        </Typography>

        {/* 에러 표시 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box mb={4}>
          <Autocomplete
            options={doJangList}
            value={selectedDoJang}
            onChange={handleDoJangChange}
            getOptionLabel={(option) => `${option.comNm} (ID: ${option.comId})`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {option.comNm}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {option.comId} • 관리자: {option.admNm || "정보 없음"}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="도장 검색 및 선택"
                placeholder="도장명을 입력하여 검색하세요"
                variant="outlined"
                fullWidth
                error={!!error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingDoJang ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            )}
            loading={isLoadingDoJang}
            disabled={isSending || isLoadingDoJang}
            noOptionsText="검색 결과가 없습니다"
            loadingText="도장 목록을 불러오는 중..."
            filterOptions={filterDoJangOptions}
            sx={{ mb: 2 }}
          />

          {/* 상태 정보 표시 */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isLoadingDoJang
              ? "도장 목록을 불러오는 중입니다..."
              : error
              ? "도장 목록을 불러오는데 실패했습니다. 새로고침 버튼을 눌러 다시 시도해주세요."
              : `검색하여 도장을 선택하세요 (총 ${stats.totalCount}개 도장)`}
          </Typography>
        </Box>

        <Button
          variant="contained"
          disableElevation
          startIcon={<SendIcon />}
          onClick={onSendEmails}
          disabled={
            !csvFile ||
            !stats.hasSelection ||
            isSending ||
            isLoadingDoJang ||
            !!error
          }
          fullWidth
          size="large"
        >
          {isSending ? "발송 중..." : "이메일 대량 발송 시작"}
        </Button>

        {isSending && (
          <Box mt={2}>
            <Typography variant="body2" mb={1}>
              진행률: {Math.round(sendProgress)}%
            </Typography>
            <LinearProgress variant="determinate" value={sendProgress} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};
